import type { PropsWithChildren } from 'react';

import type { TBreakpoints } from '@/frontend/src/hooks/useBreakpoint';

import type { EPortal } from './portal';
import type {
    EGeoCountry,
    EPlaceTypes,
    IClosestLocationResult,
    ILocationResult,
} from './geo';

export type ETracking = {
    mediumTags: {
        houseNumberLevel: string;
        streetLevel: string;
        noHandover: string;
        cannotFindMyAddress: string;
    };
};

export enum EParentApp {
    'EstimaLandingPage' = 'estima_landing_page',
}

export enum EContext {
    'EstimationFunnel' = 'estimation_funnel',
}

export type TFormValues = Record<string, never>;

export enum EActiveView {
    LocationSearch = 'LOCATION_SEARCH',
    ClosestLocationSearch = 'CLOSEST_LOCATION_SEARCH',
    ClosestLocationResult = 'CLOSEST_LOCATION_RESULT',
}

export enum EActionType {
    SetAddressAutocompleteError = 'SET_ADDRESS_AUTOCOMPLETE_ERROR',
    SetActiveView = 'SET_ACTIVE_VIEW',
    SetClosestLocationResult = 'SET_CLOSEST_LOCATION_RESULT',
    SetLocationResult = 'SET_LOCATION_RESULT',
    SetShouldDisplayHouseNumber = 'SET_SHOULD_DISPLAY_HOUSE_NUMBER',
    SetIsInvalidAddress = 'SET_IS_INVALID_ADDRESS',
}

export interface IGeoProps {
    countries?: EGeoCountry[];
    placeTypes: EPlaceTypes[];
    resultsLimit?: number;
}

export interface IState {
    context: EContext;
    parentApp?: EParentApp;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formValues: Record<string, any>;
    locationResult: ILocationResult;
    geo: IGeoProps;
    portal: EPortal;
    ctaButtonInfo: {
        url: string;
    };
    tracking?: ETracking;
    mobileBreakpoint?: TBreakpoints;
    addressAutocompleteError?: boolean;
    closestLocationResult: IClosestLocationResult;
    activeView: EActiveView;
    shouldDisplayHouseNumber: boolean;
    isInvalidAddress: boolean;
}

export interface ISetAddressAutocompleteError {
    type: EActionType.SetAddressAutocompleteError;
    payload: boolean;
}

export interface ISetClosestLocationResult {
    type: EActionType.SetClosestLocationResult;
    payload: IClosestLocationResult;
}

export interface ISetActiveView {
    type: EActionType.SetActiveView;
    payload: EActiveView;
}

export interface ISetLocationResult {
    type: EActionType.SetLocationResult;
    payload: ILocationResult;
}

export interface ISetShouldDisplayHouseNumber {
    type: EActionType.SetShouldDisplayHouseNumber;
    payload: boolean;
}

export interface ISetIsInvalidAddress {
    type: EActionType.SetIsInvalidAddress;
    payload: boolean;
}

export type TAction =
    | ISetActiveView
    | ISetAddressAutocompleteError
    | ISetClosestLocationResult
    | ISetIsInvalidAddress
    | ISetLocationResult
    | ISetShouldDisplayHouseNumber;

export type IAppContext = PropsWithChildren<
    Pick<
        IState,
        | 'context'
        | 'ctaButtonInfo'
        | 'geo'
        | 'mobileBreakpoint'
        | 'parentApp'
        | 'portal'
        | 'tracking'
    >
>;

export interface ICtaButtonInfo {
    url: string;
}
